"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
const date_fns_1 = require("date-fns");
const PaymentUtilities_1 = require("../PaymentUtilities");
const DateUtilities_1 = require("../DateUtilities");
const CreditCardUtilities_1 = require("../CreditCardUtilities");
/** @ignore common type names used by utility */
const typenames = {
    creditCardPayment: 'CreditCardPayment',
    pendingTransaction: 'CreditCardPendingTransaction',
    postedTransaction: 'CreditCardPostedTransaction',
    declinedTransaction: 'CreditCardDeclinedTransaction'
};
/** Max recent displayable transaction */
exports.MAX_RECENT_DISPLAYABLE_TRANSACTIONS = 5;
/**
 * Business use null to notate `statementDate` of `current` `statementPeriod` in service requests
 */
exports.currentStatementEndDate = null;
/**
 * Display title to denote current statement period.
 * It is prepended to statementDates;
 * this is used in computing `statementStartDate` and segmentation
 */
exports.currentStatementDisplayTitle = 'Since Last Statement';
/**
 * Transaction status; combination of `CreditCardPaymentStatus` and `Pending`/`Posted`/`Declined`
 */
exports.TransactionStatus = Object.assign({}, PaymentUtilities_1.CreditCardPaymentStatus, { declined: 'Declined' });
/**
 * Checks if an account has transactions
 * @param payments list of CreditCardPayments
 * @param statementDates list of statement dates
 * @param postedTransactions list of `CreditCardPostedTransactions`
 * @param pendingTransactions list of `CreditCardPendingTransactions`
 * @param declinedTransactions list of `CreditCardDeclinedTransactions`
 * @returns true if the account has transactions - this is if any of the following conditions are met:
 * Check for any `pending` `CreditCardPayments`, any `CreditCardTransaction`, and more `StatementDate` with potential more transactions
 */
exports.hasTransactions = (payments, statementDates, 
// tslint:disable-next-line: no-any
pendingTransactions, postedTransactions, declinedTransactions) => {
    const hasStatements = statementDates.length > 0;
    const hasPayments = !!payments.find(payment => payment.status === exports.TransactionStatus.pending);
    const hasPending = !!pendingTransactions && pendingTransactions.length > 0;
    const hasPosted = !!postedTransactions && postedTransactions.length > 0;
    const hasDeclined = !!declinedTransactions && declinedTransactions.length > 0;
    const containsTransactions = hasPending || hasPosted || hasDeclined;
    return hasPayments || containsTransactions || hasStatements;
};
/**
 * @ignore
 * Filter transactions dated before date
 * @param transactions list of transactions
 * @param date date to be compared with
 * @returns transactions dated before date
 */
const transactionsBeforeDate = (transactions, date) => transactions.filter(datedTransaction => datedTransaction.date && date_fns_1.isBefore(datedTransaction.date, date));
/**
 * @ignore
 * Filter transactions dated after date; null dated items as well
 * @param transactions list of transactions
 * @param date date to be compared with
 * @returns transactions dated after or null date
 */
const transactionsAfterDate = (transactions, date) => transactions.filter(datedTransaction => datedTransaction.date === null || date_fns_1.isAfter(datedTransaction.date, date));
/**
 * @ignore
 * Filter transactions between 2 dates; inclusive of `startDate` and `endDate`
 * @param transactions list of transactions
 * @param startDate earlier date to be compared with
 * @param endDate later date to be compared with
 * @returns transactions between 2 dates; inclusive of `startDate` and `endDate`
 */
const transactionsBetweenDate = (transactions, startDate, endDate) => transactions.filter(datedTransaction => datedTransaction.date && date_fns_1.isWithinRange(datedTransaction.date, startDate, endDate));
/**
 * @ignore
 * Compute description for `CreditCardPayment` transaction
 * @param origin payment origin
 * @returns computed description for `creditCardPayment` transaction
 */
const creditCardPaymentTransactionDescription = (origin) => {
    const paymentType = origin === CreditCardUtilities_1.displayOrigin.autopay ? 'AUTOPAY' : 'ONE-TIME';
    return `PAYMENT - THANK YOU (${paymentType})`;
};
/**
 * @ignore
 * Normalize payment transactions to displayable transactions
 * @param payments list of payment transactions
 * @returns list of normalized displayable transactions
 */
const paymentToDisplayableTransaction = (payments) => (payments.map(({ amount, status, date, origin }) => ({
    __typename: typenames.creditCardPayment,
    description: creditCardPaymentTransactionDescription(origin),
    value: amount.formatted,
    status,
    date
})));
/**
 * @ignore
 * Filter and normalizationfor selected status of `CreditCardPayment`
 * @param payments list of payment transactions
 * @param status status to be filtered
 * @returns list of normalized displayable transactions
 */
const displayablePaymentsForDatesForStatus = (payments, status) => {
    const paymentsForStatus = payments.filter(payment => payment.status === status);
    return paymentToDisplayableTransaction(paymentsForStatus);
};
/**
 * @ignore
 * Convert type of transaction to `TransactionStatus`; ONLY `pending` status is used
 * @param typename type name to be converted
 * @returns `TransactionStatus` `Pending` status
 */
const statusForType = (typename) => {
    // tested using 'rewire'
    /* istanbul ignore next  */
    switch (typename) {
        case typenames.declinedTransaction:
            return exports.TransactionStatus.declined;
        case typenames.postedTransaction:
            return exports.TransactionStatus.posted;
        default:
            return exports.TransactionStatus.pending;
    }
};
/**
 * @ignore
 * Normalize `Pending` and `declined` transactions to `displyable` transactions
 * @param transactions list of transactions; `__typename` `PendingCreditCardTransaction` or `DeclinedCreditCardTransaction`
 * @returns Displayable transactions
 */
const toDisplayableTransactions = (transactions) => ((transactions || []).map(({ __typename, description, amount, transactionDate }) => ({
    __typename,
    description,
    status: statusForType(__typename),
    date: transactionDate,
    value: amount.formatted,
})));
/**
 * @ignore
 * Normalize `Posted` transactions to `displayable` transactions
 * @param transactions list of `PostedCreditCardTransactions`
 * @returns `Displayable` transactions
 */
const toDisplayableFinalizedTransactions = (transactions) => ((transactions || []).map(({ __typename, description, amount, postDate, transactionDate, isPayment, isReturn }) => ({
    __typename: isPayment ? typenames.creditCardPayment : __typename,
    description,
    status: isReturn ? exports.TransactionStatus.returned : exports.TransactionStatus.posted,
    date: postDate || transactionDate,
    value: amount.formatted,
})));
/**
 * @ignore
 * Normalize and sort descending (most recent first) `pending`, `posted`, `declined` (unused) and payment transactions
 * @param payments list of payment transactions
 * @param transactions interface for `pending`, `posted` and `declined` (unused) transactions
 * @returns Sorted displyable transactions
 */
const displayableTransactions = (payments, transactions) => {
    const pendingPayments = displayablePaymentsForDatesForStatus(payments, exports.TransactionStatus.pending);
    const displayablePendingTransactions = toDisplayableTransactions(transactions.pending);
    const displayablePostedTransactions = toDisplayableFinalizedTransactions(transactions.posted);
    return pendingPayments
        .concat(displayablePendingTransactions, displayablePostedTransactions)
        .sort((lhs, rhs) => DateUtilities_1.compareDescending(lhs.date, rhs.date));
};
/**
 * @ignore
 * Compute transactions for statement period
 * @param transactions transactions (sorted)
 * @param statementEndDate end date for the statement period
 * @param statementDates list of statement end dates; used to compute statement start date
 * @returns Sorted displayable transactions within statement period
 */
const displayableTransactionsForStatementDate = (transactions, statementEndDate, statementDates) => {
    const selectedDates = statementStartEndDate(statementEndDate || '', statementDates);
    const isWithinFirstStatement = statementDates.length === 1 && statementEndDate === exports.currentStatementDisplayTitle;
    // no statementDates
    if (!selectedDates || (!selectedDates.endDate && !selectedDates.startDate) || isWithinFirstStatement) {
        return transactions;
    }
    const { startDate, endDate } = selectedDates;
    // first statement period
    if (!startDate && endDate) {
        return transactionsBeforeDate(transactions, endDate);
    }
    // current statement period
    if (!endDate && startDate) {
        return transactionsAfterDate(transactions, startDate);
    }
    // base case
    /* istanbul ignore else  */
    if (startDate && endDate) {
        return transactionsBetweenDate(transactions, startDate, endDate);
    }
    else {
        // All cases for inputs are covered by conditions above
        return [];
    }
};
/**
 * Compute transactions for statement period from payments and transactions
 * @param payments payment transactions
 * @param transactions interface for `pending`, `posted` and `declined`(unused) transactions
 * @param statementEndDate end date for the statement period
 * @param statementDates list of statement end dates; used to compute statement start date
 * @returns Sorted displayable transactions within statement period
 */
exports.transactionsForDate = (payments, transactions, statementEndDate, statementDates) => {
    if (!exports.hasTransactions(payments, statementDates, transactions.pending, transactions.posted, transactions.declined)) {
        return [];
    }
    const displayTransactions = displayableTransactions(payments, transactions);
    return displayableTransactionsForStatementDate(displayTransactions, statementEndDate, statementDates);
};
/**
 * Prepend item to sorted statement dates; allow any string to denote `current`
 * @param currentStatement string used to denote `current` statement
 * @param StatementDate list of `statmentDates`; will be sorted in decending order before prepend `currentStatement`
 * @returns list of sorted statement dates sorted most recent first
 */
exports.prependCurrentStatementDate = (currentStatement, statementDates) => {
    const sortedStatementDates = statementDates.slice()
        .sort(DateUtilities_1.compareDescending);
    return [currentStatement].concat(sortedStatementDates);
};
/**
 * @ignore
 * Compute `statementStartDate` and `statementEndDate`
 * @param transactions transactions (sorted)
 * @param statementEndDate end date for the statement period
 * @param statementDates list of statement end dates; used to compute statement start date
 * @returns statement start date and end date
 */
const statementStartEndDate = (endDate, statementDates) => {
    const indexOfEndDate = statementDates.indexOf(endDate);
    // couldn't find statementEndDate in statementDates; exit due to data error
    if (indexOfEndDate < 0) {
        return null;
    }
    // first statement; null startDate
    if (indexOfEndDate === statementDates.length - 1) {
        return {
            startDate: null,
            // increment 1 day because statementEndDate is inclusive
            endDate: date_fns_1.format(date_fns_1.addDays(endDate, 1), 'YYYY-MM-DD')
        };
    }
    // compute startDate by using previous endDate
    const previousEndDate = statementDates[indexOfEndDate + 1];
    const startDate = date_fns_1.format(previousEndDate, 'YYYY-MM-DD');
    // current statement; null endDate
    return {
        startDate,
        endDate: indexOfEndDate > 0 ? endDate : null
    };
};
/**
 * Segment transactions by day
 * @param transactions dated transactions (sorted)
 * @returns list of segmentation of transactions by date
 */
exports.segmentTransactionsByDay = (transactions) => {
    const uniqueDates = new Set(transactions.map(transaction => transaction.date || ''));
    return Array.from(uniqueDates)
        .map(date => {
        const transactionsForDay = transactions.filter(transaction => transaction.date === date);
        return {
            date,
            transactions: transactionsForDay
        };
    });
};
